import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { openModal } from "react-url-modal";
import { TopBar } from "@/components";
import { TopBarItem } from "@/components/TopBar/types";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchGetGamesList,
  fetchGetAppliedGames,
  fetchGetCreatedGames,
  fetchGetApplicationsCount,
  selectGames,
  selectAppliedGames,
  selectCreatedGames,
  selectApplicationsCount,
  selectGamesLoading,
  setGames,
  setGamesLoading,
} from "@/redux/games";
import { getTokenFromStorage } from "@/utils";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";
import { openAuthModal } from "@/utils/authModal/openAuthModal";
import { AvailableGamesScreen } from "../components/AvailableGamesScreen/index";
import { MyGamesScreen } from "../components/MyGamesScreen/index";
import { filterPastGames } from "@/utils/games/gameHelpers";
import styles from "./Games.module.scss";

export const Games: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const windowWidth = useCurrentWidth();
  const breakPoint = MediaBreakPoints.MOBILE_LARGE;

  const games = useAppSelector(selectGames);
  const appliedGames = useAppSelector(selectAppliedGames);
  const createdGames = useAppSelector(selectCreatedGames);
  const applicationsCount = useAppSelector(selectApplicationsCount);
  const isLoading = useAppSelector(selectGamesLoading);

  const [activeTab, setActiveTab] = useState<"available" | "my">("available");
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useEffect(() => {
    if (isAuthModalOpen) {
      openAuthModal(dispatch);
      setIsAuthModalOpen(false);
    }
  }, [isAuthModalOpen, dispatch]);

  const topBarItems: TopBarItem[] = [
    {
      id: "available",
      text: "Доступные игры",
      path: "available",
    },
    {
      id: "my",
      text: "Мои игры",
      path: "my",
      badge: applicationsCount > 0 ? applicationsCount : undefined,
    },
  ];

  useEffect(() => {
    dispatch(setGamesLoading(true));
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "my") {
      setActiveTab("my");
      const token = getTokenFromStorage();
      if (token) {
        loadGames();
      }
    } else if (path === "available") {
      setActiveTab("available");
    } else if (path === "applied") {
      // Redirect old "applied" path to "my"
      navigate("/padel/games/my", { replace: true });
    } else if (/^\d+$/.test(path || '')) {
      // Deeplink по ID игры — открываем модалку
      setActiveTab("available");
      openModal({ name: "joinGame", params: { idGame: path!, fromDeeplink: "true" } });
    } else if (path === "games" || location.pathname === "/padel/games") {
      setActiveTab("available");
      if (windowWidth > breakPoint) {
        navigate("/padel/games/available", { replace: true });
      }
    }
  }, [location.pathname, navigate, windowWidth, breakPoint]);

  const loadGames = async () => {
    const token = getTokenFromStorage();
    try {
      dispatch(setGamesLoading(true));

      const allGames = await dispatch(fetchGetGamesList()).unwrap();

      if (token) {
        const applied = await dispatch(fetchGetAppliedGames()).unwrap();
        await dispatch(fetchGetCreatedGames()).unwrap();
        await dispatch(fetchGetApplicationsCount()).unwrap();

        const filtered = allGames.filter(
          (game) => !applied.some((appliedGame) => appliedGame.id === game.id)
        );
        const activeFiltered = filterPastGames(filtered);
        dispatch(setGames(activeFiltered));
      } else {
        const activeGames = filterPastGames(allGames);
        dispatch(setGames(activeGames));
      }

      dispatch(setGamesLoading(false));
    } catch (error) {
      console.error("Error loading games:", error);
      dispatch(setGamesLoading(false));
    }
  };

  const handleTabChange = (item: TopBarItem) => {
    const token = getTokenFromStorage();
    if (item.id === "my" && !token) {
      setIsAuthModalOpen(true);
      return;
    }
    navigate(`/padel/games/${item.id}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "available":
        return (
          <AvailableGamesScreen
            listGames={games}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
      case "my":
        return (
          <MyGamesScreen
            createdGames={createdGames}
            appliedGames={appliedGames}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
      default:
        return (
          <AvailableGamesScreen
            listGames={games}
            setIsRequiredAuthOpened={setIsAuthModalOpen}
          />
        );
    }
  };

  return (
    <>
      {windowWidth > breakPoint ? (
        <div className={styles.wrapper}>
          <TopBar
            items={topBarItems}
            basePath="/padel/games"
            onItemClick={handleTabChange}
            breakPoint={breakPoint}
            alwaysTabs
          />
          <div className={styles.content}>{renderContent()}</div>
        </div>
      ) : (
        <div className={styles.mobileWrapper}>
          <TopBar
            items={topBarItems}
            basePath="/padel/games"
            onItemClick={handleTabChange}
            breakPoint={breakPoint}
            alwaysTabs
          />
          <div className={styles.mobileContent}>{renderContent()}</div>
        </div>
      )}
    </>
  );
};
